import { userRoleIconMap } from '@app/modules/admin/screens/users/components/UserRoleIconMap';
import { User } from '@mi-front/core';
import { Box, Chip } from '@mui/material';
import { AutocompleteElement, useFormContext } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { getAppUserDisplayName } from '../../helpers/user';

export interface UserSelectFieldProps {
  name: string;
  label?: string;
  multiple?: boolean;
  disabled?: boolean;
  required?: boolean;
  users: readonly User[];
  placeholder?: string;
  noOptionsText?: string;
  loading?: boolean;
  filterOptions?: (options: User[], { inputValue }: { inputValue: string }) => User[];
  getOptionDisabled?: (option: User) => boolean;
  helperText?: string;
  error?: boolean;
  size?: 'small' | 'medium';
}

export function UserSelectField({
  name,
  label,
  multiple = false,
  disabled = false,
  required = false,
  users,
  placeholder,
  noOptionsText,
  loading = false,
  filterOptions,
  getOptionDisabled,
  helperText,
  error,
  size = 'medium',
}: UserSelectFieldProps) {
  const { formatMessage } = useIntl();
  const { control } = useFormContext();

  const defaultFilterOptions = (
    options: User[],
    { inputValue }: { inputValue: string },
  ): User[] => {
    if (!inputValue) return options;

    const searchTerm = inputValue.toLowerCase();
    return options.filter((user) => {
      const displayName = getAppUserDisplayName(user).toLowerCase();
      const email = user.email.toLowerCase();
      return displayName.includes(searchTerm) || email.includes(searchTerm);
    });
  };

  const defaultGetOptionDisabled = (option: User) => {
    return false;
  };

  const sortedUsers = [...users].sort((a, b) => {
    const nameA = getAppUserDisplayName(a, 'long').toLowerCase();
    const nameB = getAppUserDisplayName(b, 'long').toLowerCase();
    return nameA.localeCompare(nameB);
  });

  return (
    <AutocompleteElement
      name={name}
      control={control}
      options={sortedUsers}
      multiple={multiple}
      loading={loading}
      required={required}
      label={
        label ||
        formatMessage({
          id: 'common.users',
          defaultMessage: 'Usuarios',
        })
      }
      textFieldProps={{
        helperText,
        error,
        disabled,
        placeholder:
          placeholder ||
          formatMessage({
            id: 'common.actions.select',
            defaultMessage: 'Seleccionar...',
          }),
      }}
      autocompleteProps={{
        fullWidth: true,
        size: size,
        filterOptions: filterOptions || defaultFilterOptions,
        getOptionDisabled: getOptionDisabled || defaultGetOptionDisabled,
        noOptionsText:
          noOptionsText ||
          formatMessage({
            id: 'common.noOptions',
            defaultMessage: 'No se encontraron resultados',
          }),
        getOptionLabel: (option: User) => {
          if (typeof option === 'string') return option;
          return getAppUserDisplayName(option, 'long');
        },
        isOptionEqualToValue: (option: User, value: User) => {
          return option.id === value?.id;
        },
        renderOption: (props, option: User) => (
          <li {...props} key={option.id}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: size === 'small' ? 0.75 : 1,
              }}
            >
              {userRoleIconMap[option.role] && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    '& svg': {
                      fontSize: size === 'small' ? '1rem' : undefined,
                    },
                  }}
                >
                  {userRoleIconMap[option.role]}
                </Box>
              )}
              <Box>
                <Box
                  sx={{
                    fontWeight: 500,
                    fontSize: size === 'small' ? '0.875rem' : undefined,
                  }}
                >
                  {getAppUserDisplayName(option, 'long')}
                </Box>
                <Box
                  sx={{
                    fontSize: size === 'small' ? '0.75rem' : '0.875rem',
                    color: '#666',
                  }}
                >
                  {option.email}
                </Box>
              </Box>
            </Box>
          </li>
        ),
        renderTags: multiple
          ? (value: User[], getTagProps) =>
              value.map((option: User, index: number) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    {...tagProps}
                    label={getAppUserDisplayName(option, 'long')}
                    // size="small"
                    // variant="outlined"
                    // color="primary"
                    onDelete={tagProps.onDelete}
                  />
                );
              })
          : undefined,
      }}
    />
  );
}
